import { computed, inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { APP_CONFIG } from '@core/common-legacy';
import { TellentCurrentUserSelectors, TellentMembersActions } from '@core/tellent-data';
import { TellentAccessService, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  AppRecruitment,
  Seat,
  SeatKind,
  TellentIAppConfig,
  TellentMember,
} from '@core/tellent-types';
import {
  AddEmployeeForPreonboardingPayload,
  AddEmployeeWithoutAccessPayload,
} from '@core/tellent-user-invitation-types';

@Injectable({ providedIn: 'root' })
export class UserInvitationFacade {
  private readonly store = inject(Store);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly appConfig = <TellentIAppConfig>inject(APP_CONFIG);

  private readonly coreHrSeat = this.store.selectSignal(
    TellentCurrentUserSelectors.seatByKindSelector(SeatKind.CoreHR),
  );
  private readonly currentMembership = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );

  public companyLoginPage = computed(() => {
    const app = <AppRecruitment>(
      this.currentMembership()?.seats.find(seat => seat.app.kind === SeatKind.Recruitment)!.app
    );
    const subdomain = app.data.careersSite?.url.split('.')[0];
    const authUrl = this.appConfig.tellentAuthUrl;

    return `${authUrl}/company/sign-in/${subdomain}`;
  });

  public downloadPasswords(members: TellentMember[]): void {
    const membersGuids = members.map((member: TellentMember) => member.guid);
    this.store.dispatch(TellentMembersActions.downloadPasswords({ payload: { membersGuids } }));
  }

  public addEmployeeForPreonboarding(members: Partial<AddEmployeeForPreonboardingPayload>): void {
    const { access, appGuid } = <Seat>this.coreHrSeat();
    const payload = <AddEmployeeForPreonboardingPayload>{
      members,
      seats: [
        {
          appGuid,
          access,
          roleExternalId: 'employee',
        },
      ],
      sendInvitation: false,
    };

    if (this.tellentAccessService.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(payload);
    } else {
      this.membersDataFacade.inviteAppMember(payload);
    }
  }

  public addEmployeeWithoutAccess(members: Partial<AddEmployeeWithoutAccessPayload>): void {
    const { access, appGuid } = <Seat>this.coreHrSeat();
    const payload = <AddEmployeeWithoutAccessPayload>{
      members,
      seats: [
        {
          appGuid,
          access,
          roleExternalId: 'employee',
        },
      ],
      sendInvitation: false,
    };

    if (this.tellentAccessService.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(payload);
    } else {
      this.membersDataFacade.inviteAppMember(payload);
    }
  }
}
